import React, { useEffect, useState, useCallback } from "react";
import { useLicense } from "../../context/licenseContext";
import apiClient from "../../api/apiClient";
import "./ComplianceDocuments.css";

const DOCUMENT_TYPES = ["Business License", "Certificate of Insurance", "Tax Certificate", "Other"];

const ComplianceDocuments = ({ userId, setHeaderTitle }) => {
    const { currentLicense } = useLicense();
    const licenseNo = currentLicense?.licenseNo;

    const [documents, setDocuments] = useState([]);
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(true); 
    const [error, setError] = useState(null);

    const [showUploadForm, setShowUploadForm] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedType, setSelectedType] = useState(DOCUMENT_TYPES[0]);
    const [customType, setCustomType] = useState("");
    const [notes, setNotes] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);

    const [deleteConfirm, setDeleteConfirm] = useState(null);

    useEffect(() => {
        setHeaderTitle("Documents");
    }, [setHeaderTitle]);

    const fetchDocuments = useCallback(async () => {
        if (!licenseNo) return;
        try {
            const res = await apiClient.get(`/compliance-docs?licenseNo=${licenseNo}`);
            setDocuments(res.data);
        } catch (err) {
            console.error("Failed to fetch documents:", err);
            setError("Failed to load documents.");
        }
    }, [licenseNo]);

    useEffect(() => {
        if (!licenseNo) return;
        apiClient
            .get(`/permissions/my-permissions?licenseNo=${licenseNo}`)
            .then((res) => setPermissions(res.data))
            .catch(() => setPermissions({ can_view_compliance_docs: 0, can_edit_compliance_docs: 0 }));
    }, [licenseNo]);

    useEffect(() => {
        if (!licenseNo || !permissions) return;
        if (!permissions.can_view_compliance_docs) {
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchDocuments().finally(() => setLoading(false));
    }, [licenseNo, permissions, fetchDocuments]);

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile) return;

        const docType = selectedType === "Other" ? customType.trim() : selectedType;
        if (!docType) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("documentType", docType);
            if (notes.trim()) formData.append("notes", notes.trim());

            await apiClient.post("/compliance-docs/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    licenseno: licenseNo,
                },
            });

            setShowUploadForm(false);
            setSelectedType(DOCUMENT_TYPES[0]);
            setCustomType("");
            setNotes("");
            setSelectedFile(null);
            await fetchDocuments();
        } catch (err) {
            const msg = err.response?.data?.error || "Upload failed. Please try again.";
            alert(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleView = async (docId) => {
        try {
            const res = await apiClient.get(`/compliance-docs/${docId}/view`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
        } catch (err) {
            alert("Failed to open document.");
        }
    };

    const handleDelete = async (docId) => {
        try {
            await apiClient.delete(`/compliance-docs/${docId}`);
            setDeleteConfirm(null);
            await fetchDocuments();
        } catch (err) {
            alert("Failed to delete document.");
        }
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (!licenseNo) {
        return <div className="comp-docs__loading">Select a license to view documents.</div>;
    }

    if (loading) {
        return <div className="comp-docs__loading">Loading documents...</div>;
    }

    if (permissions && !permissions.can_view_compliance_docs) {
        return <div className="comp-docs__error">You do not have permission to view compliance documents.</div>;
    }

    const canEdit = permissions?.can_edit_compliance_docs === 1;

    return (
        <div className="comp-docs-page">
        <div className="comp-docs">
            <div className="comp-docs__header">
                <h1 className="comp-docs__title">Compliance Documents</h1>
                {canEdit && !showUploadForm && (
                    <button className="comp-docs__upload-btn" onClick={() => setShowUploadForm(true)}>
                        + Upload Document
                    </button>
                )}
            </div>

            {error && <div className="comp-docs__error">{error}</div>}

            {showUploadForm && (
                <form className="comp-docs__upload-form" onSubmit={handleUpload}>
                    <div className="comp-docs__form-row">
                        <div className="comp-docs__form-group">
                            <label>Document Type</label>
                            <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                                {DOCUMENT_TYPES.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        {selectedType === "Other" && (
                            <div className="comp-docs__form-group">
                                <label>Custom Type</label>
                                <input
                                    type="text"
                                    value={customType}
                                    onChange={(e) => setCustomType(e.target.value)}
                                    placeholder="Enter document type"
                                    maxLength={100}
                                    required
                                />
                            </div>
                        )}
                        <div className="comp-docs__form-group">
                            <label>File</label>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => setSelectedFile(e.target.files[0] || null)}
                                required
                            />
                        </div>
                    </div>
                    <div className="comp-docs__form-group">
                        <label>Notes (optional)</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add any relevant notes about this document"
                            maxLength={1000}
                        />
                    </div>
                    <div className="comp-docs__form-actions">
                        <button
                            type="button"
                            className="comp-docs__cancel-btn"
                            onClick={() => {
                                setShowUploadForm(false);
                                setSelectedFile(null);
                                setNotes("");
                                setCustomType("");
                                setSelectedType(DOCUMENT_TYPES[0]);
                            }}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="comp-docs__submit-btn" disabled={uploading}>
                            {uploading ? "Uploading..." : "Upload"}
                        </button>
                    </div>
                </form>
            )}

            {documents.length === 0 ? (
                <div className="comp-docs__empty">
                    No compliance documents uploaded yet.
                    {canEdit && " Click \"Upload Document\" to add your first document."}
                </div>
            ) : (
                <div className="comp-docs__table-wrap">
                    <table className="comp-docs__table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>File Name</th>
                                <th>Format</th>
                                <th>Uploaded By</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documents.map((doc) => (
                                <tr key={doc.id}>
                                    <td>{doc.documentType}</td>
                                    <td>{doc.fileName}</td>
                                    <td><span className="comp-docs__type-badge">{doc.fileType}</span></td>
                                    <td>{doc.uploaderName || "—"}</td>
                                    <td>{formatDate(doc.created_at)}</td>
                                    <td>
                                        <div className="comp-docs__actions">
                                            <button className="comp-docs__view-btn" onClick={() => handleView(doc.id)}>
                                                View
                                            </button>
                                            {canEdit && (
                                                <button
                                                    className="comp-docs__delete-btn"
                                                    onClick={() => setDeleteConfirm(doc)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {deleteConfirm && (
                <div className="comp-docs__confirm-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="comp-docs__confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Document</h3>
                        <p>Are you sure you want to delete "{deleteConfirm.fileName}"? This action cannot be undone.</p>
                        <div className="comp-docs__confirm-actions">
                            <button className="comp-docs__cancel-btn" onClick={() => setDeleteConfirm(null)}>
                                Cancel
                            </button>
                            <button className="comp-docs__delete-btn" onClick={() => handleDelete(deleteConfirm.id)}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </div>
    );
};

export default ComplianceDocuments;
